import { useEffect } from "react";

/**
 * Custom hook to disable copy, cut, and paste operations on a component
 * @param {boolean} isEnabled - Whether to enable/disable clipboard restrictions
 * @param {React.RefObject} containerRef - Ref to the container element
 */
export const useDisableClipboard = (isEnabled = true, containerRef = null) => {
  useEffect(() => {
    if (!isEnabled) return;

    const targetElement = containerRef?.current || document;

    const handleAction = e => {
      e.preventDefault();
      return false;
    };

    const handleKeyDown = e => {
      // * Preventing Ctrl+C/X/V (Windows/Linux) & Cmd+C/X/V (Mac)
      if (
        (e.ctrlKey || e.metaKey) &&
        ["c", "x", "v"].includes(e.key.toLowerCase())
      ) {
        e.preventDefault();

        // * Clear clipboard after a short delay to prevent data from being pasted elsewhere
        setTimeout(() => {
          try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
              void navigator.clipboard.writeText("").catch(() => {});
            }
          } catch {}
        }, 100);
        return false;
      }
    };

    // Add event listeners
    targetElement.addEventListener("cut", handleAction, true);
    targetElement.addEventListener("copy", handleAction, true);
    targetElement.addEventListener("paste", handleAction, true);
    targetElement.addEventListener("keydown", handleKeyDown, true);
    targetElement.addEventListener("contextmenu", handleAction, true);
    document.body.style.userSelect = "none";
    document.body.style.webkitUserSelect = "none";

    // Cleanup
    return () => {
      targetElement.removeEventListener("cut", handleAction, true);
      targetElement.removeEventListener("copy", handleAction, true);
      targetElement.removeEventListener("paste", handleAction, true);
      targetElement.removeEventListener("keydown", handleKeyDown, true);
      targetElement.removeEventListener("contextmenu", handleAction, true);
      document.body.style.userSelect = "auto";
      document.body.style.webkitUserSelect = "auto";
    };
  }, [isEnabled, containerRef]);
};
